import { useCallback, useLayoutEffect, useRef, useState } from "react";

const getAnchor = (menuEl) =>
	menuEl?.closest(".shift-item, .need-cell, .plan-person-card-item");

const getFixedContainingBlock = (el) => {
	let node = el?.parentElement;

	while (node && node !== document.documentElement) {
		const style = window.getComputedStyle(node);

		if (style.transform !== "none") return node;
		if (style.perspective !== "none") return node;
		if (style.filter !== "none") return node;

		node = node.parentElement;
	}

	return null;
};

const toContainingBlockCoords = (viewportTop, viewportLeft, containingBlock) => {
	if (!containingBlock) {
		return { top: viewportTop, left: viewportLeft };
	}

	const cbRect = containingBlock.getBoundingClientRect();

	return {
		top: viewportTop - cbRect.top,
		left: viewportLeft - cbRect.left,
	};
};

export function useShiftHoverMenuPosition() {
	const containerRef = useRef(null);
	const [positionStyle, setPositionStyle] = useState({
		visibility: "hidden",
		position: "fixed",
	});

	const updatePosition = useCallback(() => {
		const menuEl = containerRef.current;
		const anchor = getAnchor(menuEl);
		if (!menuEl || !anchor) return;

		const containingBlock = getFixedContainingBlock(menuEl);
		const anchorRect = anchor.getBoundingClientRect();
		const menuWidth = menuEl.offsetWidth;
		const menuHeight = menuEl.offsetHeight;
		const isNeedCell = anchor.classList.contains("need-cell");
		const isPersonCard = anchor.classList.contains("plan-person-card-item");
		const bottomOffset = isPersonCard ? 14 : 16;

		let viewportTop;
		let viewportLeft;

		if (isNeedCell) {
			// Mitad del menú dentro de la celda, mitad sobresaliendo por el borde inferior
			viewportTop = anchorRect.bottom - menuHeight / 2;
			viewportLeft = anchorRect.left + anchorRect.width / 2 - menuWidth / 2;
		} else {
			viewportTop = anchorRect.bottom + bottomOffset - menuHeight;
			viewportLeft = anchorRect.left + anchorRect.width / 2 - menuWidth / 2;
		}

		const { top, left } = toContainingBlockCoords(
			viewportTop,
			viewportLeft,
			containingBlock
		);

		setPositionStyle({
			position: "fixed",
			top: `${top}px`,
			left: `${left}px`,
			zIndex: 1000,
			visibility: "visible",
		});
	}, []);

	useLayoutEffect(() => {
		updatePosition();

		const frame = window.requestAnimationFrame(updatePosition);

		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.cancelAnimationFrame(frame);
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [updatePosition]);

	return { containerRef, positionStyle };
}
